import { useState, useEffect } from "react";
import { Modal, Row, Col, Tooltip } from "antd";
import { Flex, Text, Checkbox, Skeleton, Box } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import Button from "components/Button";
import Field from "components/Input/Field";
import SelectBooleano from "components/Input/SelectBooleano";
import SelectPessoas from "components/Input/SelectPessoas";
import { TipoEntidadeEnum } from "resources/enums/conta";
import SelectUnidade from "components/Input/SelectUnidade";
import PrimaryButton from "components/Button/PrimaryButton";
import InputMask from "components/Input/InputMask";
import { isValidCPF } from "@brazilian-utils/brazilian-utils";
import { isValidEmail } from "infra/helpers/isValidEmail";

import {
  TipoParticipante,
  ParticipanteForm,
} from "features/assembleia/typings";
import { showSuccess, showErrorAxios } from "infra/helpers/alerta";
import { useSaveAssembleiaParticipante } from "features/assembleia/hooks";
import useTotalPesoUnidades from "features/unidades/useTotalPesoUnidades";
import usePessoaUnidadesVoto from "core/features/pessoas/hooks/usePessoaUnidadesVoto";
import { FaInfoCircle } from "react-icons/fa";
import SelectGrupoUnidade from "components/Input/SelectGrupoUnidade";
import _ from "lodash";

interface ModalAssembleiaParticipantesProps {
  isOpen: boolean;
  onClose?: () => any;
  onSaved: () => any;
  votacaoId: string;
}

function ModalAssembleiaParticipantes({
  isOpen,
  onClose,
  onSaved,
  votacaoId,
}: ModalAssembleiaParticipantesProps) {
  const [realizarCheckin, setRealizarCheckin] = useState(false);

  const { handleSubmit, watch, control, setValue } = useForm<ParticipanteForm>({
    defaultValues: { tipoParticipante: TipoParticipante.Morador },
  });

  const { mutate: handleSave, isLoading: isLoadingSave } =
    useSaveAssembleiaParticipante(votacaoId);

  const { data: totalPeso, isFetching: isFetchingPeso } = useTotalPesoUnidades(
    watch("unidades") ?? []
  );

  const tipoParticipante = watch("tipoParticipante");
  const pessoaId = watch("pessoaId");
  const unidadesSelecionadas = watch("unidades");

  const { data: unidades, isFetching: isFetchingUnidades } =
    usePessoaUnidadesVoto(pessoaId);

  useEffect(() => {
    if (unidades !== undefined) {
      setValue("unidades", unidades);
    }
  }, [unidades, setValue]);

  const onSubmit = async (data: ParticipanteForm) => {
    handleSave(
      {
        ...data,
        realizarCheckin,
        ehMorador: data.tipoParticipante === TipoParticipante.Morador,
      },
      {
        onSuccess: () =>
          showSuccess(`Participante salvo com sucesso`).then(onSaved),
        onError: (err) => showErrorAxios(err),
      }
    );
  };

  return (
    <Modal
      visible={isOpen}
      onCancel={onClose}
      width="800px"
      footer={
        <Box display="flex">
          <Checkbox
            isChecked={realizarCheckin}
            onChange={() => setRealizarCheckin((prev) => !prev)}
          >
            Realizar check-in após salvar
          </Checkbox>

          <Box
            display="flex"
            gridGap={6}
            justifyContent="flex-end"
            flex={1}
            mt={8}
          >
            <Button onClick={onClose} disabled={isLoadingSave}>
              Cancelar
            </Button>
            <PrimaryButton
              onClick={() => handleSubmit(onSubmit)()}
              isLoading={isLoadingSave}
              disabled={isLoadingSave}
            >
              Salvar
            </PrimaryButton>
          </Box>
        </Box>
      }
    >
      <form onSubmit={(e) => handleSubmit(onSubmit)(e)}>
        <Text fontSize={"18px"} fontWeight={"bold"} mb="16px">
          Cadastrar votante
        </Text>
        <Box mb="8px">
          <Row gutter={16}>
            <Col sm={10}>
              <Field
                control={control}
                label="É morador do condomínio?"
                name="tipoParticipante"
                rules={{ required: "Campo obrigatório" }}
                render={({ field }) => <SelectBooleano {...field} />}
              />
            </Col>

            {tipoParticipante === TipoParticipante.Visitante ? null : (
              <Col sm={14}>
                <Field
                  shouldUnregister
                  label="Selecionar o morador"
                  control={control}
                  name="pessoaId"
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field, isInvalid }) => (
                    <SelectPessoas
                      {...field}
                      isInvalid={isInvalid}
                      tipoEntidades={[TipoEntidadeEnum.Condomino]}
                    />
                  )}
                />
              </Col>
            )}
          </Row>
        </Box>
        <Box mb="16px">
          <Field
            name="procurador"
            control={control}
            render={({ field }) => (
              <Flex>
                <Checkbox
                  checked={!!field.value}
                  onChange={() => field.onChange(!field.value)}
                >
                  <Flex gap={2}>
                    <Text fontSize="12px">Procurador temporário</Text>

                    <Tooltip
                      title={
                        <>
                          Ao marcar isso, essa pessoa terá apenas acesso
                          temporário nas unidades às quais ainda não tinha
                          acesso, e será removida das mesmas após a votação.{" "}
                          <br />
                          <b>
                            Para isso funcionar, a votação tem que ter uma data
                            limite.
                          </b>
                        </>
                      }
                      overlayInnerStyle={{
                        width: "500px",
                      }}
                    >
                      <FaInfoCircle
                        fontSize={14}
                        style={{
                          display: "inline-block",
                          marginTop: 2,
                        }}
                      />
                    </Tooltip>
                  </Flex>
                </Checkbox>
              </Flex>
            )}
          />
        </Box>

        {tipoParticipante === TipoParticipante.Visitante ? (
          <>
            <Box mb="16px">
              <Row gutter={16}>
                <Col sm={10}>
                  <Field
                    control={control}
                    label="Nome Completo"
                    name="nome"
                    rules={{ required: "Campo obrigatório" }}
                  />
                </Col>
                <Col sm={7}>
                  <Field
                    control={control}
                    label="Data de nascimento"
                    name="dataNascimento"
                    inputProps={{
                      type: "date",
                    }}
                    rules={{ required: "Campo obrigatório" }}
                  />
                </Col>
                <Col sm={7}>
                  <Field
                    label="CPF"
                    control={control}
                    name="documento"
                    rules={{
                      required: "Campo obrigatório",
                      validate: (v) => isValidCPF(v) || "CPF inválido",
                    }}
                    render={({ field, isInvalid }) => (
                      <InputMask
                        {...field}
                        isInvalid={isInvalid}
                        mask="999.999.999-99"
                        placeholder="XXX.XXX.XXX-XX"
                      />
                    )}
                  />
                </Col>
              </Row>
            </Box>

            <Box mb="16px">
              <Row gutter={16}>
                <Col sm={10}>
                  <Field
                    label="Celular"
                    control={control}
                    name="celular"
                    render={({ field }) => {
                      return (
                        <InputMask
                          {...field}
                          mask="(99) 99999-9999"
                          placeholder="(XX) XXXXX-XXXX"
                        />
                      );
                    }}
                  />
                </Col>
                <Col sm={14}>
                  <Field
                    label={"Email"}
                    control={control}
                    name="email"
                    inputProps={{
                      placeholder: "seuemail@email.com",
                    }}
                    rules={{
                      required: "Campo obrigatório",
                      validate: (v) => isValidEmail(v) || "Email inválido",
                    }}
                  />
                </Col>
              </Row>
            </Box>
          </>
        ) : null}

        <Text fontSize={"18px"} fontWeight={"bold"} mb="16px" mt={10}>
          Unidade que serão representadas
        </Text>
        <Box as={Row} gutter={16}>
          <Box mb="4px" as={Col} xs={7}>
            <Field
              shouldUnregister
              label="Grupos de unidade"
              control={control}
              name="grupoUnidades"
              render={({ field, isInvalid }) => (
                <SelectGrupoUnidade
                  placeholder="Adicionar por grupo"
                  isInvalid={isInvalid}
                  value={[]}
                  incluirUnidades={"1"}
                  onChange={(v, option) => {
                    const unidadesGrupo: string[] =
                      option.unidades?.map((u: { id: string }) => u.id) ?? [];

                    const currentUnidades = Array.isArray(unidadesSelecionadas)
                      ? unidadesSelecionadas
                      : [];

                    const newUnidades = unidadesGrupo.filter(
                      (unidade) => !currentUnidades.includes(unidade)
                    );

                    const newValue = _.uniq([
                      ...currentUnidades,
                      ...newUnidades,
                    ]);

                    setValue("unidades", newValue);
                  }}
                />
              )}
            />
          </Box>
          <Box mb="4px" as={Col} xs={17}>
            <Field
              shouldUnregister
              label="Unidade(s)"
              control={control}
              name="unidades"
              rules={{ required: "Campo obrigatório" }}
              render={({ field, isInvalid }) => (
                <SelectUnidade
                  mode="multiple"
                  isInvalid={isInvalid}
                  allowClear
                  {...field}
                />
              )}
            />
          </Box>
        </Box>

        <Flex direction="column" mb="3" mt={6}>
          <Flex color="#768398" fontSize="18px">
            PESO TOTAL:
          </Flex>
          <Flex fontSize="18px" fontWeight="bold">
            {!isFetchingPeso && !isFetchingUnidades ? totalPeso ?? 0 : null}{" "}
            {isFetchingPeso || isFetchingUnidades ? (
              <Skeleton mt="6px" w="25px" h="15px" />
            ) : null}
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
}

export default ModalAssembleiaParticipantes;
